/**
 * VersionChecker service
 *
 * Checks if a new version of the application has been deployed by comparing
 * the current build version with the latest version available on the CDN.
 * If a new version is detected, triggers a page reload to fetch the new assets.
 */

import { APP_ENVIRONMENT, BUILD_VERSION } from '@/services/Constants';

// Build URL from environment - points to where static assets are hosted
const BUILD_URL = import.meta.env.VITE_BUILD_URL || '';

// Cache the fetch promise to avoid multiple simultaneous requests
let versionCheckPromise: Promise<string | null> | null = null;
let lastCheckTime = 0;
let isCheckInProgress = false;
const CHECK_COOLDOWN_MS = 30000; // Don't check more than once per 30 seconds

/**
 * Fetches the latest build version from the CDN
 * @returns The latest build version string, or null if fetch fails
 */
async function fetchLatestVersion(): Promise<string | null> {
	if (!BUILD_URL) {
		return null;
	}

	try {
		// Add cache-busting query param to ensure we get the latest version
		const url = `${BUILD_URL}/latest/build.txt?t=${Date.now()}`;
		const response = await fetch(url, {
			method: 'GET',
			cache: 'no-store',
		});

		if (!response.ok) {
			console.warn('[VersionChecker] Failed to fetch latest version:', response.status);
			return null;
		}

		const version = await response.text();
		return version.trim();
	} catch (error) {
		console.warn('[VersionChecker] Error fetching latest version:', error);
		return null;
	}
}

/**
 * Checks if a new version is available and reloads the page if so.
 * This function is safe to call frequently - it includes cooldown logic.
 *
 * @returns true if a reload was triggered, false otherwise
 */
export async function checkVersionAndReload(): Promise<boolean> {
	// Skip check for local development
	if (
		APP_ENVIRONMENT === 'local' ||
		!BUILD_VERSION ||
		BUILD_VERSION === '<VITE_BUILD_VERSION_CODE>'
	) {
		return false;
	}

	// If a check is already in progress, wait for it instead of returning early
	if (isCheckInProgress && versionCheckPromise) {
		try {
			const latestVersion = await versionCheckPromise;
			if (latestVersion && latestVersion !== BUILD_VERSION) {
				console.info(
					`[VersionChecker] New version detected. Current: ${BUILD_VERSION}, Latest: ${latestVersion}. Reloading...`,
				);
				window.location.reload();
				return true;
			}
			return false;
		} catch {
			return false;
		}
	}

	// Respect cooldown to avoid excessive requests (only when no check is in progress)
	const now = Date.now();
	if (now - lastCheckTime < CHECK_COOLDOWN_MS) {
		return false;
	}

	// Start a new check
	isCheckInProgress = true;
	versionCheckPromise = fetchLatestVersion();

	try {
		const latestVersion = await versionCheckPromise;

		if (!latestVersion) {
			return false;
		}

		// Compare versions - if different, a new version is deployed
		if (latestVersion !== BUILD_VERSION) {
			console.info(
				`[VersionChecker] New version detected. Current: ${BUILD_VERSION}, Latest: ${latestVersion}. Reloading...`,
			);
			window.location.reload();
			return true;
		}

		return false;
	} catch {
		return false;
	} finally {
		// Update cooldown timer and clear in-progress state after check completes
		lastCheckTime = Date.now();
		isCheckInProgress = false;
		versionCheckPromise = null;
	}
}
